import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import { Player } from '../Player/Player';
import {
  getAssetShareUrl,
  getAssetTags,
} from '../../shared/helpers/assetHelpers';
import { usePreviousValue } from '../utils/usePreviousValue';
import { AssetDetails } from '../AssetDetails/AssetDetails';
import { getAssetProps } from '../../helpers/getAssetProps';
import {
  LIVERPOOL_FC_ASSET_ID,
  LiverpoolTimetable,
} from '../LiverpoolTimetable/LiverpoolTimetable';
import { useViewportType } from '../utils/useViewportType';
import { observeViewportResize } from '../../utils/viewport/observeViewportResize';
import { playerContainer } from '../../player/playerContainer';
import { AssetContainer } from '../AssetContainer/AssetContainer';
import { usePaywallState } from '../utils/usePaywallState';
import { AssetMetadata } from '../AssetMetadata/AssetMetadata';
import { PaywalledAssetSplash } from '../Splash/PaywalledAssetSplash/PaywalledAssetSplash';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { VFC } from 'react';

import * as styles from './PlayerWithPaywalledAssetDetails.module.scss';

interface PlayerWithPaywalledAssetDetailsProps {
  asset: SvpAssetAdditional;
  startTime?: number;
}

const PlayerWithPaywalledAssetDetailsBase: VFC<
  PlayerWithPaywalledAssetDetailsProps
> = ({ asset, startTime }) => {
  const [isPlayerCollapsed, setIsPlayerCollapsed] = useState(true);
  const [isTimetableCollapsed, setIsTimetableCollapsed] = useState(false);
  const [isTimetableExpanded, setIsTimetableExpanded] = useState(false);
  const [timetableMaxHeight, setTimetableMaxHeight] = useState<number>();
  const { isMobile: isMobileViewport } = useViewportType();
  const previousAsset = usePreviousValue(asset);
  const { paywallState } = usePaywallState(asset);

  // reset the state when moving to a different asset
  useEffect(() => {
    if (asset.id !== previousAsset.id) {
      setIsPlayerCollapsed(true);
    }
  }, [asset, previousAsset]);

  const { description, additional } = asset;

  const { chapters } = additional;
  const tags = useMemo(() => getAssetTags(asset), [asset]);

  useEffect(() => {
    setIsTimetableCollapsed(!isPlayerCollapsed);
  }, [isPlayerCollapsed]);

  // measure timetable when player is collapsed, so it stays the same height sliding into the side
  const timetableRef = useCallback(
    (node) => {
      if (node && isPlayerCollapsed) {
        setTimetableMaxHeight(node.clientHeight);
      }
    },
    [isPlayerCollapsed]
  );

  useEffect(() => {
    if (isPlayerCollapsed) {
      return;
    }

    const { stopObserving } = observeViewportResize(() => {
      setTimetableMaxHeight(playerContainer.getDimensions().height);
    });

    return () => stopObserving();
  }, [isPlayerCollapsed]);

  return (
    <AssetContainer
      asset={asset}
      disableAssetBackground={isPlayerCollapsed}
      className={classNames(styles.container, {
        [styles.isPlayerCollapsed]: isPlayerCollapsed,
      })}
    >
      {(paywallState.isFetching || paywallState.hasAccess) &&
        !isPlayerCollapsed && (
          <Player
            asset={asset}
            className={styles.player}
            startTime={startTime}
          />
        )}
      {isPlayerCollapsed ? (
        <PaywalledAssetSplash asset={asset} />
      ) : (
        <AssetDetails
          tags={tags}
          chapters={chapters}
          shareUrl={getAssetShareUrl(asset)}
          {...getAssetProps(asset)}
          description={description}
          metadata={<AssetMetadata asset={asset} />}
        />
      )}
      {asset.id === LIVERPOOL_FC_ASSET_ID &&
        (isMobileViewport ? (
          <LiverpoolTimetable
            variant="list"
            ref={timetableRef}
            className={styles.timetableList}
            sidebarCollapsed={isTimetableCollapsed}
            onSidebarCollapse={setIsTimetableCollapsed}
            listExpanded={isTimetableExpanded}
            onListExpand={setIsTimetableExpanded}
          />
        ) : (
          <div className={styles.timetableSidebarWrapper}>
            <LiverpoolTimetable
              variant="sidebar"
              ref={timetableRef}
              className={styles.timetableSidebar}
              style={{
                maxHeight: isPlayerCollapsed ? undefined : timetableMaxHeight,
              }}
              sidebarCollapsed={isTimetableCollapsed}
              onSidebarCollapse={setIsTimetableCollapsed}
              listExpanded={isTimetableExpanded}
              onListExpand={setIsTimetableExpanded}
            />
          </div>
        ))}
    </AssetContainer>
  );
};
PlayerWithPaywalledAssetDetailsBase.displayName =
  'PlayerWithPaywalledAssetDetailsBase';

export const PlayerWithPaywalledAssetDetails = memo(
  PlayerWithPaywalledAssetDetailsBase
);
